// [Codex 2026-07-02] S99 mobile drawer trim: approved rows only, once-per-visit greeting, and account strip.
import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  CalendarDays,
  ChevronDown,
  History,
  LogOut,
  Newspaper,
  Settings,
  Stadium,
  Users,
} from "lucide-react";
import { FintheonBrandLockup } from "../brand/FintheonBrandLockup";
import { TraderNametag } from "../TraderNametag";
import { useAuth } from "../../contexts/AuthContext";
import { useSettings } from "../../contexts/SettingsContext";
import { useBackend } from "../../lib/backend";
import { LOADING_GREETINGS } from "../loading/loading-globe-config";
import type { SurfaceNavTab } from "../../lib/surface-capabilities";
import {
  formatRelativeDate,
  type ConversationSummary,
} from "../chat/session-history-utils";
import { NAV_ITEMS_MAP } from "./sidebar-nav-model";
import {
  PUBLIC_PROFILE_UPDATED_EVENT,
  publicProfileDraftFromProxVoice,
  readPublicProfileDraft,
  writePublicProfileDraft,
  type PublicProfileDraft,
} from "../../lib/public-profile-store";
import {
  readUserAvatarUrl,
  readUserDisplayName,
  readUserInitials,
} from "../../lib/user-profile-metadata";
import { WEEKLY_WALKTHROUGH_EVENTS } from "../../lib/weekly-walkthroughs";
import { FintheonNumericText } from "../numbers/FintheonNumericText";

type MobileConsiliumView = "chat" | "forum" | "arbitrum";

interface MobileUnderlayDrawerProps {
  open: boolean;
  activeTab: SurfaceNavTab;
  activeConsiliumView: MobileConsiliumView;
  onClose: () => void;
  onTabChange: (tab: SurfaceNavTab) => void;
  onConsiliumView: (view: MobileConsiliumView) => void;
  onNotificationCenterToggle: () => void;
  onLogout: () => void;
}

const MOBILE_DRAWER_GREETING_KEY = "fintheon:mobile-drawer-greeting-index";
const MOBILE_IDENTITY_AVATAR_NAME_LIMIT = "Nicharacci".length;

function readSessionGreetingIndex() {
  if (typeof window === "undefined") return 0;
  try {
    const existing = window.sessionStorage.getItem(MOBILE_DRAWER_GREETING_KEY);
    const parsed = existing ? Number(existing) : Number.NaN;
    if (Number.isInteger(parsed) && parsed >= 0) {
      return parsed % LOADING_GREETINGS.length;
    }
    const next = Math.floor(Math.random() * LOADING_GREETINGS.length);
    window.sessionStorage.setItem(MOBILE_DRAWER_GREETING_KEY, String(next));
    return next;
  } catch {
    return 0;
  }
}

export function MobileUnderlayDrawer({
  open,
  activeTab,
  activeConsiliumView,
  onClose,
  onTabChange,
  onConsiliumView,
  onLogout,
}: MobileUnderlayDrawerProps) {
  const { user } = useAuth();
  const { traderName, alertConfig } = useSettings();
  const backend = useBackend();
  const displayedTraderName =
    traderName.trim() ||
    readUserDisplayName(user?.user_metadata) ||
    user?.email?.split("@")[0] ||
    "Trader";
  const [publicProfile, setPublicProfile] = useState<PublicProfileDraft>(() =>
    readPublicProfileDraft(),
  );
  const traderAvatarUrl =
    publicProfile.avatarUrl || readUserAvatarUrl(user?.user_metadata);
  const traderInitials = readUserInitials(displayedTraderName);
  const showMobileIdentityAvatar =
    displayedTraderName.trim().length <= MOBILE_IDENTITY_AVATAR_NAME_LIMIT;
  const [greetingIndex] = useState(readSessionGreetingIndex);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historySessions, setHistorySessions] = useState<ConversationSummary[]>(
    [],
  );
  const swipeStartRef = useRef<{ x: number; y: number } | null>(null);
  const runAndClose = (action: () => void) => {
    action();
    onClose();
  };
  const openProfileSettings = () => {
    runAndClose(() => {
      onTabChange("settings");
      window.setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent(WEEKLY_WALKTHROUGH_EVENTS.settingsTab, {
            detail: { tab: "profile" },
          }),
        );
      }, 80);
    });
  };

  useEffect(() => {
    const onProfileUpdated = (event: Event) => {
      setPublicProfile(
        (event as CustomEvent<PublicProfileDraft>).detail ??
          readPublicProfileDraft(),
      );
    };
    window.addEventListener(PUBLIC_PROFILE_UPDATED_EVENT, onProfileUpdated);
    return () =>
      window.removeEventListener(
        PUBLIC_PROFILE_UPDATED_EVENT,
        onProfileUpdated,
      );
  }, []);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    backend.proxVoice
      .getProfile()
      .then((response) => {
        if (cancelled) return;
        const nextProfile = publicProfileDraftFromProxVoice(response.profile);
        setPublicProfile(nextProfile);
        writePublicProfileDraft(nextProfile);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [backend.proxVoice, user]);

  useEffect(() => {
    if (!open || !historyOpen) return;
    let cancelled = false;
    setHistoryLoading(true);
    backend.ai
      .listConversations({ surface: "chat" })
      .then((sessions) => {
        if (!cancelled) setHistorySessions(sessions ?? []);
      })
      .catch(() => {
        if (!cancelled) setHistorySessions([]);
      })
      .finally(() => {
        if (!cancelled) setHistoryLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [backend, historyOpen, open]);

  const handleSelectHistorySession = (id: string) => {
    runAndClose(() => {
      window.dispatchEvent(
        new CustomEvent("fintheon:chat-load-session", { detail: { id } }),
      );
    });
  };

  const handleTouchStart = (event: React.TouchEvent<HTMLElement>) => {
    const touch = event.touches[0];
    swipeStartRef.current = touch
      ? { x: touch.clientX, y: touch.clientY }
      : null;
  };

  const handleTouchEnd = (event: React.TouchEvent<HTMLElement>) => {
    const start = swipeStartRef.current;
    swipeStartRef.current = null;
    if (!start) return;
    const touch = event.changedTouches[0];
    if (!touch) return;
    const deltaX = touch.clientX - start.x;
    const deltaY = Math.abs(touch.clientY - start.y);
    if (deltaX < -44 && deltaY < 42) onClose();
  };

  return (
    <div className="pointer-events-none absolute inset-0 z-0 md:hidden">
      <aside
        aria-hidden={!open}
        data-mobile-underlay-drawer={open ? "open" : "closed"}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        className={`fintheon-side-surface fintheon-drawer-left pointer-events-auto absolute bottom-2.5 left-2.5 top-[calc(env(safe-area-inset-top)+10px)] flex w-[min(300px,calc(100vw-72px))] flex-col overflow-hidden rounded-[16px] border border-[var(--fintheon-accent)]/10 px-2 py-3 transition-[transform,opacity] duration-[330ms] ease-[cubic-bezier(0.32,0.72,0,1)] ${
          open
            ? "translate-x-0 opacity-100"
            : "-translate-x-[calc(100%+20px)] opacity-70"
        }`}
      >
        <div className="px-3 pb-2 pt-1">
          <FintheonBrandLockup
            title="Fintheon"
            className="text-[17px] font-semibold uppercase tracking-normal text-[var(--fintheon-brand-grey)]"
            iconClassName="h-5 w-5 opacity-90"
            iconSize={20}
            gap={8}
            style={{
              fontFamily:
                "'Doto', 'Readable Digits', var(--font-heading), ui-monospace, monospace",
            }}
          />
          <p
            key={greetingIndex}
            className="mobile-underlay-subtitle mt-1"
            style={{
              fontFamily:
                "'Doto', 'Readable Digits', var(--font-heading), ui-monospace, monospace",
            }}
          >
            {LOADING_GREETINGS[greetingIndex]}
          </p>
        </div>

        <nav
          className="mobile-underlay-nav flex flex-none flex-col"
          aria-label="Mobile navigation"
          data-mobile-underlay-nav="true"
        >
          <DrawerRow
            label="Desk"
            description={NAV_ITEMS_MAP.dashboard.description}
            active={activeTab === "dashboard"}
            icon={<Users />}
            onClick={() => runAndClose(() => onTabChange("dashboard"))}
          />
          <DrawerRow
            label="RiskFlow"
            description={NAV_ITEMS_MAP.riskflow.description}
            active={activeTab === "riskflow"}
            icon={<Newspaper />}
            onClick={() => runAndClose(() => onTabChange("riskflow"))}
          />
          <DrawerRow
            label="Calendar"
            description={NAV_ITEMS_MAP.econ.description}
            active={activeTab === "econ"}
            icon={<CalendarDays />}
            onClick={() => runAndClose(() => onTabChange("econ"))}
          />
          <DrawerRow
            label="Forum"
            description="Live trader floor"
            active={activeTab === "analysis" && activeConsiliumView === "forum"}
            icon={<Stadium />}
            onClick={() => runAndClose(() => onConsiliumView("forum"))}
          />
          <DrawerRow
            label="Settings"
            description="Preferences & configuration"
            active={activeTab === "settings"}
            icon={<Settings />}
            onClick={() => runAndClose(() => onTabChange("settings"))}
          />
          <MobileHistorySection
            open={historyOpen}
            loading={historyLoading}
            sessions={historySessions}
            onToggle={() => setHistoryOpen((value) => !value)}
            onSelectSession={handleSelectHistorySession}
          />
        </nav>

        <div
          className="mobile-underlay-footer-strip flex items-center justify-between gap-2"
          data-mobile-underlay-footer-row="true"
        >
          <div
            className="mobile-underlay-identity-chip fintheon-identity-chip fintheon-black-liquid-glass header-widget-glass solvys-liquid-glass flex h-10 items-center overflow-hidden rounded-[8px]"
            data-avatar={showMobileIdentityAvatar ? "visible" : "hidden"}
          >
            <button
              type="button"
              onClick={openProfileSettings}
              className="fintheon-identity-chip__name h-full min-w-0"
              aria-label={`Open profile settings for ${displayedTraderName}`}
              title="Open profile settings"
            >
              {showMobileIdentityAvatar ? (
                <span className="fintheon-identity-chip__avatar">
                  {traderAvatarUrl ? (
                    <img src={traderAvatarUrl} alt="" />
                  ) : (
                    <span className="mobile-underlay-identity-chip__avatar-fallback">
                      {traderInitials}
                    </span>
                  )}
                </span>
              ) : null}
              <TraderNametag
                name={displayedTraderName}
                variant="embedded"
                disablePulse={!(alertConfig.nametagEmoPulse ?? true)}
              />
            </button>
          </div>
          <button
            type="button"
            onClick={() => runAndClose(onLogout)}
            className="mobile-underlay-logout-button inline-flex items-center gap-2 rounded-full text-[10px] font-semibold uppercase tracking-[0.13em] text-red-300/68 transition-colors hover:text-red-300"
          >
            <LogOut className="h-4 w-4" />
            <span>Log Out</span>
          </button>
        </div>
      </aside>
    </div>
  );
}

function MobileHistorySection({
  open,
  loading,
  sessions,
  onToggle,
  onSelectSession,
}: {
  open: boolean;
  loading: boolean;
  sessions: ConversationSummary[];
  onToggle: () => void;
  onSelectSession: (id: string) => void;
}) {
  const visibleSessions = sessions.slice(0, 8);

  return (
    <section className="mobile-underlay-history-section" aria-label="History">
      <div className="mobile-underlay-history-divider-shell" aria-hidden="true">
        <span className="mobile-underlay-history-divider" />
      </div>
      <button
        type="button"
        className="mobile-underlay-history-header"
        aria-expanded={open}
        onClick={onToggle}
      >
        <span className="mobile-underlay-history-heading">
          <History className="mobile-underlay-history-heading__icon" />
          <span>History</span>
        </span>
        <ChevronDown
          className="mobile-underlay-history-chevron"
          data-open={open ? "true" : "false"}
        />
      </button>
      <div
        className="mobile-underlay-history-list"
        data-open={open ? "true" : "false"}
      >
        {loading ? (
          <p className="mobile-underlay-history-empty">Loading history...</p>
        ) : visibleSessions.length ? (
          visibleSessions.map((session) => (
            <button
              key={session.id}
              type="button"
              className="mobile-underlay-history-row"
              onClick={() => onSelectSession(session.id)}
            >
              <span className="mobile-underlay-history-row__title">
                {session.title || "Untitled conversation"}
              </span>
              <span className="mobile-underlay-history-row__time">
                <FintheonNumericText
                  text={formatRelativeDate(session.lastMessageAt)}
                  kind="date-time"
                />
              </span>
            </button>
          ))
        ) : (
          <p className="mobile-underlay-history-empty">No conversations yet</p>
        )}
      </div>
    </section>
  );
}

function DrawerRow({
  label,
  description,
  icon,
  active,
  danger,
  onClick,
}: {
  label: string;
  description: string;
  icon: ReactNode;
  active: boolean;
  danger?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-active={active ? "true" : "false"}
      className={`mobile-underlay-nav-row relative flex w-full items-center text-left uppercase transition-[color,text-shadow] active:scale-[0.99] ${
        active
          ? "text-[var(--fintheon-accent)] [text-shadow:0_0_14px_rgba(199,159,74,0.18)]"
          : danger
            ? "text-red-400/70 hover:text-red-400"
            : "text-[var(--fintheon-text)]/72 hover:text-[var(--fintheon-text)]"
      }`}
    >
      <span className="mobile-underlay-nav-row__icon">{icon}</span>
      <span className="mobile-underlay-nav-row__copy min-w-0">
        <span className="mobile-underlay-nav-row__label truncate">{label}</span>
        <span className="mobile-underlay-nav-row__description truncate">
          {description}
        </span>
      </span>
    </button>
  );
}
